// Same IRA verdict, legend and "why" copy as the /foreign-dividend-stocks hub,
// rendered inside a single ADR's card (Stock Modal Insights + SEO page).
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

const TONES = {
  ok: { label: 'IRA ok', cls: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400' },
  caution: { label: 'IRA leak', cls: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400' },
  avoid: { label: 'bad in IRA', cls: 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400' },
};

const LEGEND = [
  ['ok', 'Nothing withheld at source (or Canada, where the treaty waives it for retirement accounts)'],
  ['caution', 'Under 25% withheld — small but unrecoverable in an IRA'],
  ['avoid', '25%+ withheld at source and permanently lost in an IRA'],
];

export function IraBadge({ status, note, testId }) {
  const t = TONES[status];
  if (!t) return null;
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide cursor-help ${t.cls}`}
      title={note || undefined}
      data-testid={testId}
    >
      {t.label}
    </span>
  );
}

function AccountRow({ label, verdict, tone, children, testId }) {
  return (
    <div className="flex gap-2 text-[11px] leading-snug" data-testid={testId}>
      <span className="shrink-0 w-[108px] sm:w-[128px] font-semibold text-foreground">{label}</span>
      <span className="min-w-0">
        <span className={`font-bold ${tone}`}>{verdict}</span>
        {' — '}
        <span className="text-muted-foreground">{children}</span>
      </span>
    </div>
  );
}

export default function AdrIraSuitability({ data, testIdPrefix = 'adr-ira' }) {
  const { country, ira_warning: status, ira_note: note, withholding_source_rate: source, withholding_rate: treaty, withholding_reclaim: reclaim } = data;
  if (!status) return null;
  const isCanada = country === 'Canada';
  const zero = !source;
  const pct = (v) => (typeof v === 'number' ? `${v}%` : '—');
  const iraTone = status === 'ok' ? 'text-emerald-700 dark:text-emerald-400' : status === 'caution' ? 'text-amber-700 dark:text-amber-400' : 'text-rose-700 dark:text-rose-400';
  const iraVerdict = status === 'ok' ? (isCanada ? '0% withheld' : 'Nothing lost') : status === 'caution' ? `${pct(source)} leaks` : `${pct(source)} lost`;
  const iraWhy = isCanada
    ? 'the US–Canada treaty waives withholding entirely for US retirement accounts, so the full dividend lands.'
    : zero
      ? `${country} withholds nothing, so there is nothing to lose.`
      : `the foreign tax credit cannot be claimed inside a retirement account, so the ${pct(source)} withheld at source is gone for good with no reclaim route.`;

  return (
    <div className="rounded-md border border-border/60 bg-muted/30 p-2.5 space-y-2" data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-bold uppercase tracking-wide">IRA suitability</span>
        <IraBadge status={status} note={note} testId={`${testIdPrefix}-badge`} />
      </div>
      <p className="text-[11px] leading-relaxed text-foreground/90" data-testid={`${testIdPrefix}-note`}>{note}</p>

      <div className="space-y-1.5 pt-1 border-t border-border/50">
        <AccountRow label="Taxable account" verdict={zero ? 'Nothing withheld' : 'Mostly recovered'} tone="text-emerald-700 dark:text-emerald-400" testId={`${testIdPrefix}-taxable`}>
          {zero
            ? `${country} withholds nothing, so the gross dividend is what you receive before US tax.`
            : `the ${pct(treaty)} treaty rate comes back as a US foreign tax credit on Form 1116 (it offsets US tax you owe on the dividend).${reclaim ? ` The ${pct(source)} taken at source is above the treaty rate — the excess is reclaimable from ${country}'s tax authority, usually via your broker or a reclaim form.` : ''}`}
        </AccountRow>
        <AccountRow label="Traditional IRA / 401(k)" verdict={iraVerdict} tone={iraTone} testId={`${testIdPrefix}-ira`}>{iraWhy}</AccountRow>
        <AccountRow label="Roth IRA" verdict={iraVerdict} tone={iraTone} testId={`${testIdPrefix}-roth`}>
          {isCanada || zero ? 'same treatment as a traditional IRA — nothing is withheld.' : 'same as a traditional IRA: a Roth owes no US tax on the dividend, so there is no US tax for the credit to offset and the withheld amount is never recovered.'}
        </AccountRow>
      </div>

      <details className="text-[11px]" data-testid={`${testIdPrefix}-legend`}>
        <summary className="cursor-pointer font-semibold text-muted-foreground hover:text-foreground">How the three IRA badges work</summary>
        <div className="mt-1.5 space-y-1">
          {LEGEND.map(([k, text]) => (
            <div key={k} className="flex items-center gap-1.5 flex-wrap">
              <IraBadge status={k} testId={`${testIdPrefix}-legend-${k}`} />
              <span className="text-muted-foreground">{text}</span>
            </div>
          ))}
          <p className="text-muted-foreground leading-relaxed pt-1">
            Why: foreign withholding is recovered through the US foreign tax credit, which only offsets US tax you actually owe.
            An IRA or Roth owes no US tax on the dividend, so there is nothing for the credit to offset and the withheld amount is
            gone for good — there is no reclaim route either. Not tax advice.
          </p>
          <Link to="/foreign-dividend-stocks" className="inline-flex items-center gap-0.5 font-semibold text-cyan-700 dark:text-cyan-400 hover:underline" data-testid={`${testIdPrefix}-hub-link`}>
            Compare every ADR by country and IRA fit <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
      </details>
    </div>
  );
}
